import { AGENT_TYPES, AGENT_TYPE_LABELS, type AgentType } from "./schema";

// Task #907 — Persona-driven referrer portal foundation.
//
// A "persona" is the tailored portal experience an agent sees based
// on their structured `agent_type`. Each known agent type maps to one
// persona (immigration_attorney / broker_dealer / foreign_migration_agent)
// and unclassified rows (or rows still flagged for admin review) fall
// back to the `default` persona, which preserves today's generic
// layout and shows a soft banner asking the admin team to classify
// them.
//
// This module is the single source of truth for:
//   * which dashboard slots appear and in what order
//   * which sidebar nav entries are surfaced and in what order
//   * which structured profile fields the persona can edit
//   * the persona's onboarding checklist
//
// The same module is consumed by:
//   * server/routes/agent-portal.ts        — resolves persona on
//     `/api/agent/me` and validates writes to `personaProfileExtras`
//     on `/api/agent/profile`
//   * client/src/hooks/use-agent-auth.tsx  — exposes the resolved
//     persona to the agent UI
//   * client/src/components/agent-layout.tsx
//                                           — drives nav order
//   * client/src/pages/agent/dashboard.tsx — drives slot order
//   * client/src/pages/agent/profile.tsx   — drives persona profile
//                                            field rendering
//   * client/src/pages/admin/agents.tsx    — read-only display of
//                                            persona extras

// ---- Persona keys ---------------------------------------------------

export const PERSONA_KEYS = [
  ...AGENT_TYPES,
  "default",
] as const;
export type PersonaKey = (typeof PERSONA_KEYS)[number];

// ---- Slot / nav / field / onboarding / resource specs ---------------

// Dashboard slot kinds. Common slots (`onboarding`, `alerts`, KPI
// tiles, pipeline/recent) are reused across personas. The persona
// highlight slots (`documentsStatus`, `commissionsHighlight`) are only
// included by the persona they belong to. Deal rooms are rendered
// above these slots once the partner has signed the platform NDA.
export const DASHBOARD_SLOT_KEYS = [
  "onboarding",
  "alerts",
  "documentsStatus",
  "commissionsHighlight",
  // Task #1007 — broker-dealer-only highlight that lists projects the
  // BD has been granted compliance-underwriting access to. Surfaces
  // open compliance work (fee not paid, package not yet acknowledged
  // pass/fail) so it doesn't get lost behind the funnel.
  "bdCompliance",
  "kpiTiles",
  "investorSummary",
] as const;
export type DashboardSlotKey = (typeof DASHBOARD_SLOT_KEYS)[number];

export const NAV_KEYS = [
  "dashboard",
  "investors",
  "projects",
  "marketing",
  "materials",
  "inbox",
  "earnings",
  "documents",
  "profile",
] as const;
export type NavKey = (typeof NAV_KEYS)[number];

export interface NavSpec {
  key: NavKey;
  /** When true the nav entry is rendered with a subtle "primary"
   * highlight in the sidebar so the persona's most-used surface
   * stays visually anchored. Optional; persona configs only set it
   * for the one entry the persona should anchor on. */
  primary?: boolean;
}

export const PERSONA_FIELD_KINDS = ["text", "textarea", "multiselect"] as const;
export type PersonaFieldKind = (typeof PERSONA_FIELD_KINDS)[number];

export interface PersonaFieldSpec {
  /** Stored under this key inside `referral_agents.personaProfileExtras`. */
  key: string;
  label: string;
  kind: PersonaFieldKind;
  placeholder?: string;
  /** Free-form helper copy shown under the field. */
  hint?: string;
  /** Soft max-length used by the field schema. */
  maxLength?: number;
}

export interface OnboardingItemSpec {
  key: string;
  label: string;
  description: string;
  /** Internal href the agent is sent to when they click "Do it". */
  href: string;
  actionLabel: string;
  /** Resolves whether the step is already done from the cached
   * `/api/agent/me` payload. We pass the raw payload so the resolver
   * can inspect persona extras, coverage arrays, etc. without
   * re-fetching. */
  isComplete: (ctx: OnboardingContext) => boolean;
  /** When defined and returning false, the step is omitted from the
   * checklist entirely (not rendered, not counted toward progress).
   * Used by steps that are situational — e.g. the W-9 only applies
   * to US-based agents. Defaults to "always applicable". */
  applicability?: (ctx: OnboardingContext) => boolean;
}

export interface OnboardingContext {
  agent: {
    companyName?: string | null;
    contactFirstName?: string | null;
    contactLastName?: string | null;
    contactPhone?: string | null;
    website?: string | null;
    logoUrl?: string | null;
    licenseExpiresAt?: string | Date | null;
    w9LastUploadedAt?: string | Date | null;
    personaProfileExtras?: Record<string, unknown> | null;
  };
  hasCountries: boolean;
  hasLanguages: boolean;
  hasInvestors: boolean;
  /** Surfaced from `/api/agent/me` so onboarding doesn't have to
   * fetch the documents endpoint separately. Driven by actual
   * uploaded-document presence (Task #907) — admin-managed
   * `licenseExpiresAt` is not a substitute for the file being on
   * file. */
  hasLicenseDocument: boolean;
  hasW9Document: boolean;
  /** True when the agent's coverage list includes the US. Used by the
   * W-9 applicability predicate. License flow is unaffected. */
  isUsBased: boolean;
  /** True when the agent has accrued commission entries or a
   * non-terminal scheduled payout. Combined with `isUsBased` to keep
   * W-9 collection situational instead of a day-one blocker. */
  hasPayoutActivity: boolean;
}

export interface PersonaConfig {
  key: PersonaKey;
  /** The structured agent_type this persona is bound to. `null` for
   * the `default` persona which catches unclassified / pending rows. */
  agentType: AgentType | null;
  label: string;
  /** One-line description shown on the dashboard hero / soft banner. */
  tagline: string;
  /** Short copy explaining how this portal is tailored — used in the
   * "Tailored for …" tooltip on the layout. */
  blurb: string;
  dashboard: { slots: readonly DashboardSlotKey[] };
  nav: readonly NavSpec[];
  profileFields: readonly PersonaFieldSpec[];
  onboarding: readonly OnboardingItemSpec[];
  /** When true, the Earnings page shows per-investor commission and
   * payout tabs. False for retainer-paid personas (marketing_agent)
   * that only use Schedule B / Qualified leads on that surface. */
  showsCommissionTabs: boolean;
  /**
   * Deal-room list and download do not require the platform partner NDA.
   * The server enforces this from the firm record. The flag only hides
   * the NDA prompt in the partner UI.
   */
  skipsPlatformNdaForDealRoom: boolean;
}

// ---- Shared onboarding helpers --------------------------------------
//
// Most of the onboarding checks are identical across personas (basic
// profile filled, coverage set, first investor added). We define them
// once and reuse them inside each persona config so the personas
// themselves stay declarative.

const isProfileComplete = (ctx: OnboardingContext): boolean => {
  const a = ctx.agent;
  return (
    !!a.companyName?.trim() &&
    !!a.contactFirstName?.trim() &&
    !!a.contactLastName?.trim() &&
    !!a.contactPhone?.trim()
  );
};

// License / W-9 completion are driven by the actual uploaded
// document, not by the admin-managed `licenseExpiresAt` /
// `w9LastUploadedAt` columns (those are set when an admin reviews
// the upload — not when the agent submits it). The flags come from
// `/api/agent/me`, which queries the agent_documents table (and the
// legacy application document path) and reports presence directly.
const hasLicenseOnFile = (ctx: OnboardingContext): boolean => ctx.hasLicenseDocument;

// W-9 is situational: only US-based agents who actually have payout
// activity see the step. Exported for tests/w9-us-only.test.ts.
export const hasW9OnFile = (ctx: OnboardingContext): boolean => ctx.hasW9Document;
const isW9Applicable = (ctx: OnboardingContext): boolean =>
  ctx.isUsBased && ctx.hasPayoutActivity;

const hasCoverage = (ctx: OnboardingContext): boolean =>
  ctx.hasCountries && ctx.hasLanguages;

const hasInvestor = (ctx: OnboardingContext): boolean => ctx.hasInvestors;

const personaExtraFilled = (key: string) => (ctx: OnboardingContext): boolean => {
  const extras = ctx.agent.personaProfileExtras;
  if (!extras || typeof extras !== "object") return false;
  const value = (extras as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim().length > 0;
};

// Each onboarding step `href` deep-links to the section/anchor on
// the profile page that unblocks it (Task #907 — checklist contract:
// "each item links to the field it unblocks"). The matching ids
// (`#firm-profile`, `#coverage`, `#documents`, `#persona-fields`)
// are rendered on the corresponding `AgentCard`s in
// `client/src/pages/agent/profile.tsx`, and the page's
// `hashchange`/mount effect scrolls them into view.
const COMMON_ONBOARDING: OnboardingItemSpec[] = [
  {
    key: "complete_profile",
    label: "Complete your firm profile",
    description: "Add your company name, contact details, and phone so investors and our team can reach you.",
    href: "/agent/profile#firm-profile",
    actionLabel: "Open profile",
    isComplete: isProfileComplete,
  },
  {
    key: "set_coverage",
    label: "Set your country & language coverage",
    description: "Tell us which markets you serve and which languages you speak — we use this to route new inquiries.",
    href: "/agent/profile#coverage",
    actionLabel: "Update coverage",
    isComplete: hasCoverage,
  },
  {
    key: "upload_license",
    label: "Upload your professional license",
    description: "Compliance needs a current copy of your license to keep you in good standing.",
    href: "/agent/profile#documents",
    actionLabel: "Upload license",
    isComplete: hasLicenseOnFile,
  },
  {
    key: "upload_w9",
    label: "Upload your W-9",
    description: "Required so we can pay commissions when investors fund.",
    href: "/agent/profile#documents",
    actionLabel: "Upload W-9",
    isComplete: hasW9OnFile,
    applicability: isW9Applicable,
  },
  {
    key: "add_first_investor",
    label: "Add your first investor",
    description: "Use the investors page to add a referral or share your landing page link.",
    href: "/agent/investors",
    actionLabel: "Add investor",
    isComplete: hasInvestor,
  },
];

// ---- Persona configs ------------------------------------------------

const NAV_DEFAULT: readonly NavSpec[] = [
  { key: "dashboard" },
  { key: "investors" },
  { key: "projects" },
  { key: "marketing" },
  { key: "materials" },
  { key: "inbox" },
  { key: "earnings" },
  { key: "documents" },
  { key: "profile" },
];

const SLOTS_DEFAULT: readonly DashboardSlotKey[] = [
  "onboarding",
  "alerts",
  "kpiTiles",
  "investorSummary",
];

const IMMIGRATION_ATTORNEY: PersonaConfig = {
  key: "immigration_attorney",
  agentType: "immigration_attorney",
  label: AGENT_TYPE_LABELS.immigration_attorney,
  tagline: "Track client matters, keep compliance current, and hand investors off cleanly.",
  blurb: "Your portal leads with documents and case status so you can shepherd I-526E filings without losing track of the paperwork.",
  // Documents-first, status-first: compliance documents and the
  // pipeline/recent pane sit before generic share/marketing content.
  dashboard: {
    slots: [
      "onboarding",
      "alerts",
      "documentsStatus",
      "investorSummary",
      "kpiTiles",
    ],
  },
  // Dashboard anchors the nav; attorneys reach their compliance docs
  // via the Compliance section on the Profile tab.
  nav: [
    { key: "dashboard", primary: true },
    { key: "investors" },
    { key: "projects" },
    { key: "inbox" },
    { key: "marketing" },
    { key: "materials" },
    { key: "earnings" },
    { key: "documents" },
    { key: "profile" },
  ],
  showsCommissionTabs: true,
  skipsPlatformNdaForDealRoom: true,
  profileFields: [
    { key: "barNumber", label: "Primary bar number", kind: "text", placeholder: "e.g. 1234567", maxLength: 64, hint: "Used by our compliance team to verify your standing." },
    { key: "barStateAdmissions", label: "State bar admissions", kind: "multiselect", placeholder: "Select states", maxLength: 400, hint: "Select every state where you're admitted to practice." },
    { key: "firmName", label: "Law firm name", kind: "text", placeholder: "e.g. Smith Immigration LLP", maxLength: 200 },
  ],
  onboarding: [
    ...COMMON_ONBOARDING.slice(0, 1),
    {
      key: "set_bar_details",
      label: "Add your bar number & state admissions",
      description: "Helps our compliance team verify your standing before referrals are routed to you.",
      href: "/agent/profile#persona-fields",
      actionLabel: "Add bar info",
      isComplete: (ctx) =>
        personaExtraFilled("barNumber")(ctx) && personaExtraFilled("barStateAdmissions")(ctx),
    },
    ...COMMON_ONBOARDING.slice(1),
  ],
};

const BROKER_DEALER: PersonaConfig = {
  key: "broker_dealer",
  agentType: "broker_dealer",
  label: AGENT_TYPE_LABELS.broker_dealer,
  tagline: "Register investors, track your pipeline, and keep your firm's compliance documents current.",
  blurb: "Your portal leads with your referrals and pipeline so you can register and advance investors, with commissions and FINRA compliance a click away.",
  // Task #2075 — Lead-registration-first: until committed-investor
  // volume is meaningful, earnings/commission figures stay zero and
  // make the portal feel empty. So the dashboard now leads with the
  // referral/lead KPIs, the funnel + share toolkit, and the pipeline
  // (what we actually want partners doing), with open compliance work
  // next. The commissions/funding highlight drops to a smaller,
  // secondary card near the bottom rather than being the hero, and the
  // Dashboard (not Earnings) is the primary nav anchor.
  dashboard: {
    slots: [
      "onboarding",
      "alerts",
      "kpiTiles",
      "investorSummary",
      "bdCompliance",
      "commissionsHighlight",
    ],
  },
  nav: [
    { key: "dashboard", primary: true },
    { key: "investors" },
    { key: "projects" },
    { key: "earnings" },
    { key: "marketing" },
    { key: "materials" },
    { key: "inbox" },
    { key: "documents" },
    { key: "profile" },
  ],
  showsCommissionTabs: true,
  skipsPlatformNdaForDealRoom: false,
  profileFields: [
    { key: "crdNumber", label: "Individual CRD number", kind: "text", placeholder: "e.g. 1234567", maxLength: 64, hint: "Your individual CRD; the firm CRD goes in the next field." },
    { key: "firmCrdNumber", label: "Firm CRD number", kind: "text", placeholder: "e.g. 7654321", maxLength: 64 },
    { key: "finraFirmName", label: "FINRA-registered firm name", kind: "text", placeholder: "e.g. Acme Securities, LLC", maxLength: 200 },
    { key: "registeredStates", label: "Registered states", kind: "textarea", placeholder: "e.g. NY, NJ, CA, FL, TX", maxLength: 400, hint: "Comma-separated list of every state where you're registered to sell securities." },
    { key: "supervisoryPrincipal", label: "Supervisory principal", kind: "text", placeholder: "Name + email", maxLength: 200, hint: "Who at your firm signs off on EB-5 deal participation." },
  ],
  onboarding: [
    ...COMMON_ONBOARDING.slice(0, 1),
    {
      key: "set_crd",
      label: "Add your CRD & registered states",
      description: "Required by our compliance team before you can be paid on a deal.",
      href: "/agent/profile#persona-fields",
      actionLabel: "Add CRD info",
      isComplete: (ctx) =>
        personaExtraFilled("crdNumber")(ctx) && personaExtraFilled("registeredStates")(ctx),
    },
    {
      key: "set_firm_supervisor",
      label: "Add your FINRA firm name & supervisory principal",
      description: "We need the registered firm name and the principal who signs off on EB-5 deal participation before you can be activated for payouts.",
      href: "/agent/profile#persona-fields",
      actionLabel: "Add firm info",
      isComplete: (ctx) =>
        personaExtraFilled("finraFirmName")(ctx) && personaExtraFilled("supervisoryPrincipal")(ctx),
    },
    ...COMMON_ONBOARDING.slice(1),
  ],
};

const FOREIGN_MIGRATION_AGENT: PersonaConfig = {
  key: "foreign_migration_agent",
  agentType: "foreign_migration_agent",
  label: AGENT_TYPE_LABELS.foreign_migration_agent,
  tagline: "Drive investor referrals from your home market and convert them with co-branded materials.",
  blurb: "Your portal leads with marketing and pipeline so you can run high-volume referral campaigns from your local market.",
  dashboard: {
    slots: [
      "onboarding",
      "alerts",
      "investorSummary",
      "kpiTiles",
    ],
  },
  nav: [
    { key: "dashboard" },
    { key: "marketing", primary: true },
    { key: "materials" },
    { key: "investors" },
    { key: "projects" },
    { key: "inbox" },
    { key: "earnings" },
    { key: "documents" },
    { key: "profile" },
  ],
  showsCommissionTabs: true,
  skipsPlatformNdaForDealRoom: false,
  // Countries and languages are collected once via the Coverage selector
  // (Profile page) — they are NOT duplicated here. These fields cover
  // the license identity and the structured office address parts (filled
  // via Google address autofill on the Profile page's Migration Details
  // section). The validator accepts any key present in this list, so
  // adding structured address keys here lets the existing
  // validatePersonaProfileExtras / mergePersonaProfileExtras pipeline
  // accept them without a schema change.
  profileFields: [
    { key: "localLicenseBody", label: "Local migration license body", kind: "text", placeholder: "e.g. MARA (AU), ICCRC (CA), MOJ (CN)", maxLength: 200, hint: "Which national body licenses you to consult on migration." },
    { key: "localLicenseNumber", label: "Local migration license number", kind: "text", placeholder: "e.g. MARN 0123456", maxLength: 128 },
    { key: "officeStreet", label: "Office street address", kind: "text", maxLength: 300 },
    { key: "officeCity", label: "Office city", kind: "text", maxLength: 100 },
    { key: "officeStateRegion", label: "Office state / region", kind: "text", maxLength: 100 },
    { key: "officePostalCode", label: "Office postal code", kind: "text", maxLength: 20 },
    { key: "officeCountry", label: "Office country", kind: "text", maxLength: 100 },
    { key: "officePlaceId", label: "Office Google Place ID", kind: "text", maxLength: 300 },
  ],
  onboarding: [
    // complete_profile + set_coverage (countries AND languages via
    // the Coverage selector — the single source of truth for both).
    ...COMMON_ONBOARDING.slice(0, 2),
    {
      key: "set_license_body",
      label: "Add your local migration license body",
      description: "Lets compliance verify you and helps us surface the right localized marketing materials.",
      href: "/agent/profile#migration-details",
      actionLabel: "Add license info",
      isComplete: personaExtraFilled("localLicenseBody"),
    },
    {
      key: "set_local_license_number",
      label: "Add your local migration license number",
      description: "Compliance needs the actual license number issued by your local migration body, not just the body's name.",
      href: "/agent/profile#migration-details",
      actionLabel: "Add license number",
      isComplete: personaExtraFilled("localLicenseNumber"),
    },
    ...COMMON_ONBOARDING.slice(2),
  ],
};

// Task #3148 — retainer-paid marketing partners. Full referral
// toolkit (marketing tools, leads, pipeline, attribution) but NO
// commission machinery: the commission engine + admin rule routes
// hard-block the type server-side, and the earnings page hides
// commission/payout tabs for this persona (Qualified leads / Schedule B
// stay reachable via the shared Earnings nav entry). No licensing/
// credential fields — the only persona extra is whether they operate
// as an individual or company.
const MARKETING_AGENT: PersonaConfig = {
  key: "marketing_agent",
  agentType: "marketing_agent",
  label: AGENT_TYPE_LABELS.marketing_agent,
  tagline: "Promote projects, drive referrals, and track your pipeline's performance.",
  blurb: "Your portal leads with marketing tools and pipeline stats so you can run campaigns and see exactly which referrals convert.",
  dashboard: {
    slots: [
      "onboarding",
      "alerts",
      "investorSummary",
      "kpiTiles",
    ],
  },
  // Earnings is in the nav so Schedule B / Qualified leads is
  // discoverable; commission/payout tabs stay off via
  // showsCommissionTabs (retainer-paid — Task #3148).
  nav: [
    { key: "dashboard" },
    { key: "marketing", primary: true },
    { key: "materials" },
    { key: "investors" },
    { key: "projects" },
    { key: "inbox" },
    { key: "earnings" },
    { key: "documents" },
    { key: "profile" },
  ],
  showsCommissionTabs: false,
  skipsPlatformNdaForDealRoom: false,
  profileFields: [
    { key: "entityKind", label: "Individual or company", kind: "text", placeholder: "individual or company", maxLength: 20, hint: "Whether you market projects as an individual or through a company." },
  ],
  onboarding: [
    // complete_profile + set_coverage, then straight to the first
    // investor — no license or W-9 steps (no credentials required and
    // no commission payouts to document for the IRS).
    ...COMMON_ONBOARDING.slice(0, 2),
    ...COMMON_ONBOARDING.slice(4),
  ],
};

const DEFAULT_PERSONA: PersonaConfig = {
  key: "default",
  agentType: null,
  label: "Referral Partner",
  tagline: "Track your referrals and grow your pipeline.",
  blurb: "Your portal is using the generic layout. Once our admin team classifies your firm we'll tailor the dashboard and profile to your role.",
  dashboard: { slots: SLOTS_DEFAULT },
  nav: NAV_DEFAULT,
  showsCommissionTabs: true,
  skipsPlatformNdaForDealRoom: false,
  profileFields: [],
  onboarding: COMMON_ONBOARDING,
};

// Plain "Agent" — same toolkit as the default/foreign-agent portal,
// with no bar / CRD / primary-country extras.
const AGENT_PERSONA: PersonaConfig = {
  key: "agent",
  agentType: "agent",
  label: AGENT_TYPE_LABELS.agent,
  tagline: "Track your referrals and grow your pipeline.",
  blurb: "Your portal has the standard referral toolkit — investors, projects, marketing, and earnings.",
  dashboard: { slots: SLOTS_DEFAULT },
  nav: NAV_DEFAULT,
  showsCommissionTabs: true,
  skipsPlatformNdaForDealRoom: false,
  profileFields: [],
  onboarding: COMMON_ONBOARDING,
};

const PERSONA_REGISTRY: Record<PersonaKey, PersonaConfig> = {
  agent: AGENT_PERSONA,
  immigration_attorney: IMMIGRATION_ATTORNEY,
  broker_dealer: BROKER_DEALER,
  foreign_migration_agent: FOREIGN_MIGRATION_AGENT,
  marketing_agent: MARKETING_AGENT,
  default: DEFAULT_PERSONA,
};

export function getPersonaConfig(key: PersonaKey): PersonaConfig {
  return PERSONA_REGISTRY[key];
}

export interface PersonaResolutionInput {
  agentType: AgentType | null | undefined;
  /** When true the agent has been flagged for admin classification
   * (Task #886) — even if `agent_type` happens to be set, we keep
   * them on the `default` persona until an admin clears the flag.
   * This guards the "agent self-applied with the wrong type" case
   * where the value is present but not yet trusted. */
  adminReviewNeeded?: boolean | null | undefined;
}

/**
 * Resolve the persona for an agent. Unclassified rows (NULL
 * `agent_type`) AND rows still flagged for admin review fall back to
 * the `default` persona so they see the generic portal alongside the
 * soft banner asking the admin team to classify them. This is the
 * single fallback point so the UI never has to special-case either
 * condition.
 */
export function resolvePersona(input: PersonaResolutionInput): PersonaConfig {
  if (input.adminReviewNeeded) return DEFAULT_PERSONA;
  if (!input.agentType) return DEFAULT_PERSONA;
  return PERSONA_REGISTRY[input.agentType];
}

// ---- personaProfileExtras validation --------------------------------
//
// The DB column is permissive jsonb (so future fields ship without a
// migration), but every write goes through this validator first to
// keep the shape predictable. The validator separates submitted keys
// into two buckets:
//   * `set`   — non-empty values that should land on the column
//   * `clear` — keys explicitly submitted as empty (the partner
//               cleared the input). The merger drops these from
//               `existing` so blanking a field actually erases it
//               instead of silently retaining the prior value.
// Unknown keys (not declared by the persona's `profileFields`) are
// dropped entirely. Throws on a non-object payload, a non-string
// value, or an over-length value.

export interface PersonaExtrasUpdate {
  set: Record<string, string>;
  clear: string[];
}

export function validatePersonaProfileExtras(
  personaKey: PersonaKey,
  value: unknown,
): PersonaExtrasUpdate {
  if (value == null) return { set: {}, clear: [] };
  if (typeof value !== "object" || Array.isArray(value)) {
    throw new Error("personaProfileExtras must be an object");
  }
  const persona = PERSONA_REGISTRY[personaKey];
  const knownByKey = new Map(persona.profileFields.map((f) => [f.key, f] as const));

  const set: Record<string, string> = {};
  const clear: string[] = [];
  for (const [k, raw] of Object.entries(value as Record<string, unknown>)) {
    const field = knownByKey.get(k);
    if (!field) continue;
    if (raw == null) {
      clear.push(k);
      continue;
    }
    if (typeof raw !== "string") {
      throw new Error(`${field.label} must be a string`);
    }
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      clear.push(k);
      continue;
    }
    const max = field.maxLength ?? 1000;
    if (trimmed.length > max) {
      throw new Error(`${field.label} must be ${max} characters or fewer`);
    }
    set[k] = trimmed;
  }
  return { set, clear };
}

/**
 * Merge a validated `PersonaExtrasUpdate` into the existing column
 * value. Used by the profile PUT route so a partial update only
 * overrides the keys the agent actually touched, AND so blank
 * submissions explicitly erase the previously-stored value.
 */
export function mergePersonaProfileExtras(
  existing: Record<string, unknown> | null | undefined,
  update: PersonaExtrasUpdate,
): Record<string, string> {
  const base: Record<string, string> = {};
  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
    for (const [k, v] of Object.entries(existing)) {
      if (typeof v === "string" && v.length > 0) base[k] = v;
    }
  }
  for (const k of update.clear) {
    delete base[k];
  }
  return { ...base, ...update.set };
}

/**
 * Look up the persona-specific label for a given extras key. Used by
 * the admin agent detail view to render persona extras read-only with
 * their human label rather than the raw camelCase storage key.
 * Returns the raw key if the persona doesn't define a label for it.
 */
export function personaFieldLabel(personaKey: PersonaKey, fieldKey: string): string {
  const persona = PERSONA_REGISTRY[personaKey];
  const field = persona.profileFields.find((f) => f.key === fieldKey);
  return field?.label ?? fieldKey;
}
